import React, { useState } from 'react';

const LEVELS = ['high', 'medium', 'low', 'lower', 'minimal'];
const ms = (v) => (v ? `${v.toFixed(1)}` : '–');
const n = (v) => Math.round(v).toLocaleString('en-US');

function Spark({ hist }) {
  const W = 120, H = 28, max = 75;
  const pts = hist.map((v, i) => (v == null ? null : [i * (W / 47), H - Math.min(max, v) / max * H]));
  let d = '';
  let pen = false;
  for (const p of pts) { if (!p) { pen = false; continue; } d += `${pen ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`; pen = true; }
  const y60 = H - 60 / max * H, y30 = H - 30 / max * H;
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <line x1="0" x2={W} y1={y60} y2={y60} className="spark-ref" />
      <line x1="0" x2={W} y1={y30} y2={y30} className="spark-ref" />
      <path d={d} className="spark-line" />
    </svg>
  );
}

/** The always-on performance HUD: frame rate, frame times, draw calls, counts and the governor. */
export default function PerfHud({ hud, events, governor, building, note }) {
  const [open, setOpen] = useState(false);
  const p = hud?.perf;
  const fps = p?.fps ? Math.round(p.fps) : null;
  const rung = p?.rung ?? 0;
  const tone = fps == null ? '' : fps >= 50 ? 'ok' : fps >= 28 ? 'warn' : 'bad';
  return (
    <div className={`hud ${open ? 'hud-open' : ''}`} role="region" aria-label="Performance monitor">
      <button type="button" className="hud-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`hud-fps ${tone}`}>
          <b>{fps ?? '–'}</b><small>fps</small>
        </span>
        {hud && <Spark hist={hud.hist} />}
        <span className="hud-level">
          <span className="hud-ladder" aria-label={`governor rung ${rung} of 4`}>
            {LEVELS.map((l, i) => <i key={l} className={i <= 4 - rung ? 'on' : ''} />)}
          </span>
          <small>{p ? p.level : '…'}</small>
        </span>
        <span className="hud-toggle" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      <dl className="hud-grid">
        <div><dt>frame p50 / p90</dt><dd>{p ? `${ms(p.frameMs.p50)} / ${ms(p.frameMs.p90)} ms` : '–'}</dd></div>
        <div><dt>render CPU p90</dt><dd>{p ? `${ms(p.cpuMs.p90)} ms` : '–'}</dd></div>
        <div><dt>frame budget</dt><dd>{p ? `${ms(p.budgetMs)} ms` : '–'}</dd></div>
        <div><dt>draw calls / frame</dt><dd>{hud && hud.frames ? n(hud.draws) : '–'}</dd></div>
        <div><dt>vector shapes</dt><dd>{hud ? n(hud.counts.shapes) : '–'}</dd></div>
        <div><dt>particles live</dt><dd>{hud ? n(hud.particles) : '–'}</dd></div>
        <div><dt>governor</dt><dd>{p ? `${governor === 'auto' ? 'auto' : 'off (forced high)'} · rung ${rung}` : '–'}</dd></div>
        <div><dt>device tier</dt><dd>{p ? (p.tier ?? 'not probed') : '–'}</dd></div>
        <div className="wide"><dt>shedding now</dt><dd>{p ? knobs(p.knobs) : '–'}</dd></div>
      </dl>
      <div className="hud-log" aria-live="polite">
        {building && <p className="hud-ev">rebuilding the document…</p>}
        {note && <p className="hud-ev">{note}</p>}
        {events.length === 0 && !building && <p className="hud-ev dim">No governor steps yet. Raise the stress to make it act.</p>}
        {events.map((e) => (
          <p className="hud-ev" key={e.when + e.level}>
            <b className={e.from != null && LEVELS.indexOf(e.level) > e.from ? 'down' : 'up'}>
              {e.from != null ? `${LEVELS[e.from]} → ${e.level}` : `starts at ${e.level}`}
            </b>{' '}
            {e.fps ? `at ${Math.round(e.fps)} fps · ` : ''}{e.from != null ? e.reason : '(device tier)'}
          </p>
        ))}
      </div>
    </div>
  );
}

function knobs(k) {
  const parts = [];
  if (k.scale < 1) parts.push(`pixels ×${k.scale}`);
  if (k.rate) parts.push(`draw ≤${k.rate} fps`);
  if (k.blurStep > 1) parts.push(`blur every ${k.blurStep} px`);
  if (Number.isFinite(k.tapBudget)) parts.push(`post ≤${k.tapBudget} reads/px`);
  if (k.instFx < 1) parts.push(k.instFx ? `filter radius ×${k.instFx}` : 'filters off');
  if (k.particles < 1) parts.push(`particles ×${k.particles}`);
  parts.push(`≤${k.maxParticles.toLocaleString('en-US')} per emitter`);
  if (k.flatten > 1) parts.push(`curves ×${k.flatten}`);
  if (k.cache) parts.push('bitmap cache while moving');
  return parts.length > 1 ? parts.join(' · ') : `nothing (${parts[0]})`;
}
